import '../../App.css';
import TimeDelayDocument from '../TimeDelayDocument';
import TooltipSuggestionDocument from '../TooltipSuggestionDocument';
import TweakingTrailingWidgetDocument from '../tweaking-interface/TweakingTrailingWidgetDocument';
import MainToolbar from '../HighlightMainToolbar';
import { defaultTheme, Provider, Flex, Button, AlertDialog, DialogTrigger, View } from '@adobe/react-spectrum';
import { useEffect, useState } from 'react';
import { logHighlightedPassage } from '../../logging';
import TweakingToolbar from '../tweaking-interface/TweakingToolbar';
import UsabilityRedactDocument from './UsabilityRedactDocument';
import TweakingTimeDelayDocument from '../tweaking-interface/TweakingTimeDelayDocument';

// Interface to customize the nudge parameters
function UsabilityNudgingInterface({ condition, participantId, maxWordCount, constrainHighlights, callbacks }) {
  // TOOLBAR
  const [selectedTool, setSelectedTool] = useState("highlighter"); // current tool selected

  // WORD COUNT
  const [wordCount, setWordCount] = useState(0);
  const [progressWordCount, setProgressWordCount] = useState(0);
  const [shadowWordCount, setShadowWordCount] = useState(0);

  // HIGHLIGHTS
  const [highlights, setHighlights] = useState([]); // all of the raw selections
  const [markToTarget, setMarkToTarget] = useState({}); // which mark to target on click
  const [allowedToHighlight, setAllowedToHighlight] = useState(true); // whether the user is allowed to highlight

  // USABILITY EXPERIMENT
  const [textToHighlight, setTextToHighlight] = useState([{start: 32, end: 54, text: "lay about 50 kilometers"}, {start: 1021, end: 1071, text: "and the valley's potential for extensive irrigation"}]); // the text the participant has to highlight

  const [showError, setShowError] = useState(false); // whether the error message is shown


  // States for the redact, time delay, and trailing widget
  const [selectedLevel, setSelectedLevel] = useState('low');

  const [selectedRedact, setSelectedRedact] = useState(0); // the current redact amount selected (specific values for low, medium, and high)

  const [selectedTime, setSelectedTime] = useState(0); // the current time delay selected (specific values for low, medium, and high)
  const [lowTime, setLowTime] = useState(0); // the maximum time delay for the low level
  const [medTime, setMedTime] = useState(0); // the maximum time delay for the medium level
  const [highTime, setHighTime] = useState(0); // the maximum time delay for the high level

  const [selectedSize, setSelectedSize] = useState(0); // the current trailing widget size selected (specific values for low, medium, and high)
  const [lowSize, setLowSize] = useState(250); // the size for the low level
  const [medSize, setMedSize] = useState(250); // the size for the medium level
  const [highSize, setHighSize] = useState(250); // the size for the high level

  const passage = "The city of Teotihuacán, which lay about 50 kilometers northeast of modern-day Mexico City, began its growth by 200–100 B.C. At its height, between about A.D. 150 and 700, it probably had a population of more than 125,000 people and covered at least 20 square kilometers. It had over 2,000 apartment complexes, a great market, a large number of industrial workshops, an administrative center, a number of massive religious edifices, and a regular grid pattern of streets and buildings. Clearly, much planning and central control were involved in the expansion and ordering of this great metropolis. Moreover, the city had economic and perhaps religious contacts with most parts of Mesoamerica (modern Central America and Mexico).\nHow did this tremendous development take place, and why did it happen in the Teotihuacán Valley? Among the main factors are Teotihuacán’s geographic location on a natural trade route to the south and east of the Valley of Mexico, the obsidian resources in the Teotihuacán Valley itself, and the valley’s potential for extensive irrigation. The exact role of other factors is much more difficult to pinpoint—for instance, Teotihuacán’s religious significance as a shrine, the historical situation in and around the Valley of Mexico toward the end of the first millennium B.C., the ingenuity and foresightedness of Teotihuacán’s elite, and, finally, the impact of natural disasters, such as the volcanic eruptions of the late first millennium B.C.\nThis last factor is at least circumstantially implicated in Teotihuacán’s rise. Prior to 200 B.C., a number of relatively small centers coexisted in and near the Valley of Mexico. Around this time, the largest of these centers, Cuicuilco, was seriously affected by a volcanic eruption, with much of its agricultural land covered by lava. With Cuicuilco eliminated as a potential rival, any one of a number of relatively modest towns might have emerged as a leading economic and political power in Central Mexico. The archaeological evidence clearly indicates, though, that Teotihuacán was the center that did arise as the predominant force in the area by the first century A.D.\nIt seems likely that Teotihuacán’s natural resources—along with the city elite’s ability to recognize their potential—gave the city a competitive edge over its neighbors. The valley, like many other places in Mexican and Guatemalan highlands, was rich in obsidian. The hard volcanic stone was a resource that had been in great demand for many years, at least since the rise of the Olmecs (a people who flourished between 1200 and 400 B.C.), and it apparently had a secure market. Moreover, recent research on obsidian tools found at Olmec sites has shown that some of the obsidian obtained by the Olmecs originated near Teotihuacán. Teotihuacán obsidian must have been recognized as a valuable commodity for many centuries before the great city arose." 

  
  function countNumWords(newSelection) {
    // remove empty strings and punctuation
    var words = [];
    var punctuations = [".", ",", ":", "!", "?", "-", "(", ")", "[", "]", "{", "}", ";", "'", '"', "<", ">", "/", "\\"];
    newSelection.split(" ").forEach(element => {
      // check if the element is not empty and not a punctuation
      if (element !== "" && !punctuations.includes(element)) {
        words.push(element);
      }
    });

    return words.length;
  }

  function finishReading() {
    // logHighlightedPassage({participantId, highlights}, callbacks);
  }

  return (
    <div style={{ backgroundColor: "#f8f8f8" }}>
      <Provider theme={defaultTheme} colorScheme='light'>
        <Flex direction="row" justifyContent={'start'} alignItems={"center"} width={"100vw"} height={"100vh"} gap={"size-200"}>
          
          <Flex direction="column" gap={"size-200"} height={"100vh"} justifyContent={'start'} alignItems={"center"} margin={'auto'}>
            {condition === 'redact' ?
              <>
                <h3 style={{ textAlign: 'center' }}>Highlight text in the following passage.</h3>
                <UsabilityRedactDocument
                  redactAmount={selectedRedact}
                  countNumWords={countNumWords}
                  passage={passage}
                  allowedToHighlight={allowedToHighlight}
                  setAllowedToHighlight={setAllowedToHighlight}
                  highlights={highlights}
                  textToHighlight={textToHighlight} // the text the participant has to highlight
                  setHighlights={setHighlights}
                  selectedTool={selectedTool}
                  wordCount={wordCount}
                  setWordCount={setWordCount}
                  setProgressWordCount={setProgressWordCount}
                  setShadowWordCount={setShadowWordCount}
                  maxWordCount={maxWordCount}
                  targetWordCount={0} // nudging interaction techniques
                  markToTarget={markToTarget}
                  setMarkToTarget={setMarkToTarget}
                  showError={showError}
                  setShowError={setShowError}
                  constrainHighlights={constrainHighlights}
                  participantId={participantId}
                />
              </> :

              condition === 'time' ?
                <>
                  <h3 style={{ textAlign: 'center' }}>Highlight text in the following passage.</h3>
                  <TweakingTimeDelayDocument
                    timeDelay={selectedTime}
                    countNumWords={countNumWords}
                    passage={passage}
                    allowedToHighlight={allowedToHighlight}
                    setAllowedToHighlight={setAllowedToHighlight}
                    highlights={highlights}
                    setHighlights={setHighlights}
                    selectedTool={selectedTool}
                    wordCount={wordCount}
                    setWordCount={setWordCount}
                    setProgressWordCount={setProgressWordCount}
                    setShadowWordCount={setShadowWordCount}
                    maxWordCount={maxWordCount}
                    targetWordCount={150} // nudging interaction techniques
                    markToTarget={markToTarget}
                    setMarkToTarget={setMarkToTarget}
                    showError={showError}
                    setShowError={setShowError}
                    constrainHighlights={constrainHighlights}
                    participantId={participantId}
                  />
                </>:

                condition === 'trailing' ?
                <>
                  <h3 style={{ textAlign: 'center' }}>Highlight text in the following passage.</h3>
                  <TweakingTrailingWidgetDocument
                    mouseWidgetWidth={selectedSize}
                    countNumWords={countNumWords}
                    passage={passage}
                    allowedToHighlight={allowedToHighlight}
                    setAllowedToHighlight={setAllowedToHighlight}
                    highlights={highlights}
                    setHighlights={setHighlights}
                    selectedTool={selectedTool}
                    wordCount={wordCount}
                    setWordCount={setWordCount}
                    setProgressWordCount={setProgressWordCount}
                    setShadowWordCount={setShadowWordCount}
                    maxWordCount={maxWordCount}
                    targetWordCount={150} // nudging interaction techniques
                    markToTarget={markToTarget}
                    setMarkToTarget={setMarkToTarget}
                    showError={showError}
                    setShowError={setShowError}
                    constrainHighlights={constrainHighlights}
                    participantId={participantId}
                  />
                  </>
                : null
                }
          </Flex>

        </Flex>
      </Provider>
    </div>
  );
}

export default UsabilityNudgingInterface;